import React, { useState, useEffect } from 'react';
import { X, Edit2, Save, XCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { ParkingLot, Feedback } from '../lib/types';
import { updateParkingLot } from '../lib/api';
import { calculateFeedbackSummary } from '../lib/utils';

interface ParkingLotModalProps {
    parkingLot: ParkingLot | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: (updatedLot: ParkingLot) => void;
}

export const ParkingLotModal: React.FC<ParkingLotModalProps> = ({
    parkingLot,
    isOpen,
    onClose,
    onUpdate
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [jsonData, setJsonData] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Initialize JSON data when parking lot changes
    useEffect(() => {
        if (parkingLot) {
            setJsonData(JSON.stringify(parkingLot, null, 2));
            setError(null);
            setSuccess(null);
            setIsEditing(false);
        }
    }, [parkingLot]);

    // Handle modal close
    const handleClose = () => {
        setIsEditing(false);
        setError(null);
        setSuccess(null);
        onClose();
    };

    // Handle edit mode toggle
    const handleEditToggle = () => {
        setIsEditing(!isEditing);
        setError(null);
        setSuccess(null);
    };

    // Validate JSON data
    const validateJsonData = (data: string): { isValid: boolean; error?: string; parsedData?: any } => {
        try {
            const parsed = JSON.parse(data);

            // Check required fields
            if (!parsed._id) {
                return { isValid: false, error: 'Missing required field: _id' };
            }
            if (!parsed.lotId) {
                return { isValid: false, error: 'Missing required field: lotId' };
            }
            // Validate geometry structure
            if (!parsed.geometry) {
                return { isValid: false, error: 'Missing required field: geometry' };
            }
            if (!parsed.geometry.coordinates) {
                return { isValid: false, error: 'Missing required field: geometry.coordinates' };
            }
            if (!Array.isArray(parsed.geometry.coordinates)) {
                return { isValid: false, error: `geometry.coordinates must be an array, got: ${typeof parsed.geometry.coordinates}` };
            }

            // Validate based on geometry type
            if (parsed.geometry.type === 'Point') {
                // Point geometry: coordinates should be [lng, lat]
                if (parsed.geometry.coordinates.length !== 2) {
                    return { isValid: false, error: `Point coordinates must have exactly 2 elements, got: ${parsed.geometry.coordinates.length}` };
                }
                if (typeof parsed.geometry.coordinates[0] !== 'number' || typeof parsed.geometry.coordinates[1] !== 'number') {
                    return { isValid: false, error: 'Point coordinates must be numbers' };
                }
            } else if (parsed.geometry.type === 'MultiPolygon') {
                // MultiPolygon geometry: coordinates should be [[[[lng, lat], ...]]]
                if (parsed.geometry.coordinates.length === 0) {
                    return { isValid: false, error: 'MultiPolygon coordinates cannot be empty' };
                }
                // Basic validation - check that it's a nested array structure
                if (!Array.isArray(parsed.geometry.coordinates[0])) {
                    return { isValid: false, error: 'MultiPolygon coordinates must be nested arrays' };
                }
            } else {
                // Allow other geometry types but warn
                console.warn(`Unrecognized geometry type: ${parsed.geometry.type}`);
            }

            return { isValid: true, parsedData: parsed };
        } catch (e) {
            return { isValid: false, error: 'Invalid JSON syntax' };
        }
    };

    // Handle save
    const handleSave = async () => {
        if (!parkingLot) return;

        const validation = validateJsonData(jsonData);
        if (!validation.isValid) {
            setError(validation.error || 'Validation failed');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const updatedLot = await updateParkingLot(parkingLot._id, validation.parsedData);
            onUpdate(updatedLot);
            setSuccess('Parking lot updated successfully!');
            setIsEditing(false);
        } catch (err: any) {
            setError(err.message || 'Failed to update parking lot');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle cancel edit
    const handleCancel = () => {
        if (parkingLot) {
            setJsonData(JSON.stringify(parkingLot, null, 2));
        }
        setIsEditing(false);
        setError(null);
        setSuccess(null);
    };

    if (!isOpen || !parkingLot) {
        return null;
    }

    const feedbackSummary = calculateFeedbackSummary(parkingLot.feedback);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            {parkingLot.name || 'Unnamed Parking Lot'}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Lot ID: {parkingLot.lotId} • Address: {parkingLot.address || 'N/A'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditing ? (
                            <button
                                onClick={handleEditToggle}
                                className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                <Edit2 className="h-4 w-4" />
                                Edit
                            </button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                                >
                                    <Save className="h-4 w-4" />
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
                                >
                                    <XCircle className="h-4 w-4" />
                                    Cancel
                                </button>
                            </div>
                        )}
                        <button
                            onClick={handleClose}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {/* Success/Error Messages */}
                    {success && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                            {success}
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    {/* Feedback Summary */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Feedback Summary</h3>
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <ThumbsUp className="h-5 w-5 text-green-600" />
                                <span className="text-sm font-medium">{feedbackSummary.thumbsUp} Positive</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <ThumbsDown className="h-5 w-5 text-red-600" />
                                <span className="text-sm font-medium">{feedbackSummary.thumbsDown} Negative</span>
                            </div>
                            <div className="text-sm text-gray-600">
                                {feedbackSummary.total} total feedback • {feedbackSummary.positivePercentage}% positive
                            </div>
                        </div>
                    </div>

                    {/* JSON Data */}
                    <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">
                            {isEditing ? 'Edit Raw Data (JSON)' : 'Raw Data (JSON)'}
                        </h3>
                        {isEditing ? (
                            <textarea
                                value={jsonData}
                                onChange={(e) => setJsonData(e.target.value)}
                                className="w-full h-96 p-3 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter JSON data..."
                            />
                        ) : (
                            <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
                                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto max-h-96">
                                    {JSON.stringify(parkingLot, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Feedback Details */}
                    {parkingLot.feedback && parkingLot.feedback.length > 0 && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-3">All Feedback</h3>
                            <div className="space-y-3">
                                {parkingLot.feedback.map((feedback: Feedback, index: number) => (
                                    <div key={feedback._id || index} className="p-3 border border-gray-200 rounded-md">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                {feedback.feedbackType === 'up' ? (
                                                    <ThumbsUp className="h-4 w-4 text-green-600" />
                                                ) : feedback.feedbackType === 'down' ? (
                                                    <ThumbsDown className="h-4 w-4 text-red-600" />
                                                ) : (
                                                    <span className="text-sm text-gray-600">💬</span>
                                                )}
                                                <span className="text-sm font-medium capitalize">
                                                    {feedback.feedbackType}
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {new Date(feedback.timestamp).toLocaleString()}
                                            </span>
                                        </div>
                                        {feedback.comment && (
                                            <p className="text-sm text-gray-700 mt-1">{feedback.comment}</p>
                                        )}
                                        <div className="text-xs text-gray-500 mt-2">
                                            User: {feedback.userId} • Search: {feedback.searchId || 'N/A'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
